<script lang="ts">
import { tv } from 'tailwind-variants';

const filePreview = tv({
	slots: {
		root: '@container relative flex flex-col items-stretch overflow-hidden rounded-lg border p-3',
		header: 'flex items-center gap-1.5 overflow-hidden pe-8',
		removeAction: 'absolute inset-e-2',
	},
	variants: {
		withWaveform: {
			true: {
				header: 'text-xs text-dimmed',
				removeAction: 'top-2',
			},
			false: {
				root: 'flex gap-2',
				header: 'mb-1 h-10 border-b pt-1 pb-3 text-sm font-medium',
				removeAction: 'top-3.5',
			},
		},
	},
});

const __DsBlock = {
	hidden: true,
	category: 'file-preview',
	tags: ['audio/'],
	events: [
		{
			name: 'remove',
			payload: [],
		},
	],
}</script>

<script lang="ts" setup>
import type { FilePreviewBlockEmits, FilePreviewBlockProps } from '#layers/assets/types/file-preview';
import { computed, useTemplateRef } from 'vue';
import { useAppConfig } from '#app/config';
import { useFileCollection } from '#imports';
import { useWorkspaceAssetUrl } from '#layers/assets/composables/use-asset-url';
import { useWavesurfer } from '#layers/assets/composables/use-wavesurfer';
import { isNil } from '#layers/base/public';
import { defineBlock } from '#layers/block-engine/public';
import { RenderCollectionDisplayTemplate } from '#layers/blocks/public';
import { CollectionReads } from '#layers/data-model/public';

const { file, showRemove = true } = defineProps<FilePreviewBlockProps>();

defineEmits<FilePreviewBlockEmits>();

const MAX_WAVEFORM_FILE_SIZE = 5 * 1024 * 1024; // 5MiB

;

const appConfig = useAppConfig();
const assetURL = useWorkspaceAssetUrl(() => file);

const waveformRef = useTemplateRef('waveform');
const audioRef = useTemplateRef('audio');

// Since the waveform library does download the full file ahead of time to render the waveform we want to make sure that
// the file size is not too large.
const shouldRenderWaveform = computed(() => !!(file.fileSize && Number(file.fileSize) < MAX_WAVEFORM_FILE_SIZE));

const { isReady } = useWavesurfer({
	container: waveformRef,
	media: computed(() => shouldRenderWaveform.value ? audioRef.value : null),
	waveColor: '#e5e5e5',
	progressColor: '#262626',
	cursorWidth: 0,
	barWidth: 4,
	barGap: 4,
	barRadius: 10,
});

const ui = computed(() => filePreview({
	withWaveform: shouldRenderWaveform.value,
}));

const { collection } = useFileCollection();
</script>

<template>
	<div :class="ui.root()">
		<div :class="ui.header()">
			<template v-if="shouldRenderWaveform">
				<span class="truncate">{{ file.fileName }}</span>
				<span v-if="!isNil(file.fileSize)" class="hidden @sm:contents">
					<span>•</span>
					<BlockRender block="$builtin:DisplayFileSize" :props="{ value: file.fileSize, class: 'whitespace-nowrap' }" />
				</span>
			</template>
			<RenderCollectionDisplayTemplate v-else :collection-id="collection!.id" :item="file" :read="CollectionReads.Many" />
		</div>
		<div v-if="shouldRenderWaveform" ref="waveform" class="relative mx-3">
			<Transition leave-active-class="transition-opacity" leave-from-class="opacity-100" leave-to-class="opacity-0">
				<AudioWaveformLoading v-if="!isReady" :bar-width="4" :bar-gap="4" bar-color="#e5e5e5" />
			</Transition>
		</div>
		<MediaControls audio download :src="assetURL">
			<template #media="mediaProps">
				<audio ref="audio" :src="assetURL" v-bind="mediaProps" />
			</template>
		</MediaControls>
		<DTooltip v-if="showRemove" :text="$t('common.remove')">
			<DButton variant="ghost" color="neutral" size="sm" :icon="appConfig.ui.icons.remove" :class="ui.removeAction()" @click="$emit('remove')" />
		</DTooltip>
	</div>
</template>
